import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { Navigate } from 'react-router-dom'
import { User, ShoppingBag, Settings, Package, Clock, CheckCircle, XCircle, Edit3, Save, X, Mail, AlertTriangle } from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import { orderService, authService } from '../services/api'
import { getFeaturedImageUrl } from '../utils/imageUtils'
import { toast } from 'react-hot-toast'

const Profile = () => {
  const { isAuthenticated, user, loading } = useAuth()
  const [activeTab, setActiveTab] = useState('account')
  const [orders, setOrders] = useState([])
  const [ordersLoading, setOrdersLoading] = useState(false)
  const [ordersError, setOrdersError] = useState('')
  
  // Edit profile state
  const [isEditing, setIsEditing] = useState(false)
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: ''
  })
  const [updateLoading, setUpdateLoading] = useState(false)
  
  // Email verification state
  const [verificationLoading, setVerificationLoading] = useState(false)

  // Initialize edit form when user data is available
  useEffect(() => {
    if (user) {
      setEditForm({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        email: user.email || ''
      })
    }
  }, [user])

  // Fetch orders when orders tab is active
  useEffect(() => {
    if (activeTab === 'orders' && isAuthenticated) {
      fetchOrders()
    }
  }, [activeTab, isAuthenticated])

  const fetchOrders = async () => {
    setOrdersLoading(true)
    setOrdersError('')
    try {
      const response = await orderService.getOrders()
      setOrders(response.orders || [])
    } catch (error) {
      console.error('Error fetching orders:', error)
      setOrdersError('Failed to load orders')
    } finally {
      setOrdersLoading(false)
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered':
        return <CheckCircle className="h-5 w-5 text-green-600" />
      case 'cancelled':
      case 'refunded':
        return <XCircle className="h-5 w-5 text-red-600" />
      default:
        return <Clock className="h-5 w-5 text-yellow-600" />
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered':
        return 'bg-green-100 text-green-800'
      case 'cancelled':
      case 'refunded':
        return 'bg-red-100 text-red-800'
      case 'processing':
        return 'bg-blue-100 text-blue-800'
      case 'shipped':
        return 'bg-purple-100 text-purple-800'
      default:
        return 'bg-yellow-100 text-yellow-800'
    }
  }

  const handleEditProfile = () => {
    setIsEditing(true)
  }

  const handleCancelEdit = () => {
    setIsEditing(false)
    // Reset form to original values
    setEditForm({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      phone: user.phone || '',
      email: user.email || ''
    })
  }

  const handleSaveProfile = async () => {
    setUpdateLoading(true)
    try {
      await authService.updateProfile(editForm)
      toast.success('Profile updated successfully!')
      setIsEditing(false)
      // The auth context should refresh automatically
    } catch (error) {
      console.error('Error updating profile:', error)
      toast.error(error.message || 'Failed to update profile')
    } finally {
      setUpdateLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleResendVerification = async () => {
    if (!user?.email) return
    
    setVerificationLoading(true)
    try {
      await authService.resendVerificationCode(user.email)
      toast.success('Verification code sent to your email!')
    } catch (error) {
      console.error('Error sending verification code:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Failed to send verification code'
      toast.error(errorMessage)
    } finally {
      setVerificationLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />
  }

  const tabs = [
    { id: 'account', name: 'Account Info', icon: User },
    { id: 'orders', name: 'Order History', icon: ShoppingBag },
    { id: 'settings', name: 'Settings', icon: Settings },
  ]

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">My Profile</h1>
        
        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                <span>{tab.name}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Account Info Tab */}
        {activeTab === 'account' && (
          <div className="space-y-6">
            {/* Email Verification Notice */}
            {user && !user.isVerified && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <AlertTriangle className="h-5 w-5 text-yellow-400" />
                  </div>
                  <div className="ml-3 flex-1">
                    <h3 className="text-sm font-medium text-yellow-800">
                      Email verification required
                    </h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p>
                        Please verify your email address to secure your account and receive important notifications.
                      </p>
                    </div>
                    <div className="mt-4">
                      <button
                        onClick={handleResendVerification}
                        disabled={verificationLoading}
                        className="btn btn-sm bg-yellow-600 hover:bg-yellow-700 text-white flex items-center space-x-2"
                      >
                        {verificationLoading ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <Mail className="h-4 w-4" />
                        )}
                        <span>{verificationLoading ? 'Sending verification code...' : 'Send verification code'}</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div className="bg-white shadow rounded-lg border">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900">Account Information</h2>
                {!isEditing && (
                  <button 
                    onClick={handleEditProfile}
                    className="btn btn-outline btn-sm flex items-center space-x-2"
                  >
                    <Edit3 className="h-4 w-4" />
                    <span>Edit Profile</span>
                  </button>
                )}
              </div>
              
              <div className="px-6 py-4 space-y-4">
                {isEditing ? (
                  /* Edit Form */
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Email</label>
                        <input
                          type="email"
                          name="email"
                          value={editForm.email}
                          onChange={handleInputChange}
                          className="mt-1 input w-full"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700">First Name</label>
                        <input
                          type="text"
                          name="first_name"
                          value={editForm.first_name}
                          onChange={handleInputChange}
                          className="mt-1 input w-full"
                          placeholder="Enter first name"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Last Name</label>
                        <input
                          type="text"
                          name="last_name"
                          value={editForm.last_name}
                          onChange={handleInputChange}
                          className="mt-1 input w-full"
                          placeholder="Enter last name"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Phone</label>
                        <input
                          type="tel"
                          name="phone"
                          value={editForm.phone}
                          onChange={handleInputChange}
                          className="mt-1 input w-full"
                          placeholder="Enter phone number"
                        />
                      </div>
                    </div>
                    
                    <div className="pt-4 flex items-center space-x-3">
                      <button 
                        onClick={handleSaveProfile}
                        disabled={updateLoading}
                        className="btn btn-primary flex items-center space-x-2"
                      >
                        {updateLoading ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <Save className="h-4 w-4" />
                        )}
                        <span>{updateLoading ? 'Saving...' : 'Save Changes'}</span>
                      </button>
                      <button 
                        onClick={handleCancelEdit}
                        className="btn btn-secondary flex items-center space-x-2"
                      >
                        <X className="h-4 w-4" />
                        <span>Cancel</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Display Mode */
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Username</label>
                      <p className="mt-1 text-sm text-gray-900">{user?.username}</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Email</label>
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-sm text-gray-900">{user?.email}</p>
                        <div className="flex items-center space-x-2">
                          {user?.isVerified ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Verified
                            </span>
                          ) : (
                            <div className="flex items-center space-x-2">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                Not Verified
                              </span>
                              <button
                                onClick={handleResendVerification}
                                disabled={verificationLoading}
                                className="btn btn-sm btn-outline text-xs flex items-center space-x-1"
                              >
                                {verificationLoading ? (
                                  <LoadingSpinner size="sm" />
                                ) : (
                                  <Mail className="w-3 h-3" />
                                )}
                                <span>{verificationLoading ? 'Sending...' : 'Verify Email'}</span>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">First Name</label>
                      <p className="mt-1 text-sm text-gray-900">{user?.first_name || 'Not provided'}</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Last Name</label>
                      <p className="mt-1 text-sm text-gray-900">{user?.last_name || 'Not provided'}</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Phone</label>
                      <p className="mt-1 text-sm text-gray-900">{user?.phone || 'Not provided'}</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Role</label>
                      <p className="mt-1 text-sm text-gray-900 capitalize">{user?.isAdmin ? 'Admin' : 'Customer'}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="space-y-6">
            <div className="bg-white shadow rounded-lg border">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Order History</h2>
              </div>
              
              <div className="px-6 py-4">
                {ordersLoading ? (
                  <div className="flex justify-center py-8">
                    <LoadingSpinner size="lg" />
                  </div>
                ) : ordersError ? (
                  <div className="text-center py-8">
                    <XCircle className="mx-auto h-12 w-12 text-red-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">Error loading orders</h3>
                    <p className="mt-1 text-sm text-gray-500">{ordersError}</p>
                    <button
                      onClick={fetchOrders}
                      className="mt-4 btn btn-outline btn-sm"
                    >
                      Try Again
                    </button>
                  </div>
                ) : orders.length === 0 ? (
                  <div className="text-center py-8">
                    <Package className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No orders yet</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Your order history will appear here after you make your first purchase.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {orders.map((order) => (
                      <div key={order.id} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-4">
                            <div>
                              <h3 className="font-medium text-gray-900">Order #{order.order_number || 'N/A'}</h3>
                              <p className="text-sm text-gray-500">
                                {order.created_at ? new Date(order.created_at).toLocaleDateString() : 'Unknown date'}
                              </p>
                            </div>
                            <div className="flex items-center space-x-2">
                              {getStatusIcon(order.status)}
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(order.status)}`}>
                                {order.status ? order.status.charAt(0).toUpperCase() + order.status.slice(1) : 'Unknown'}
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-gray-900">${order.total_amount ? parseFloat(order.total_amount).toFixed(2) : '0.00'}</p>
                            <p className="text-sm text-gray-500">{order.items?.length || 0} items</p>
                          </div>
                        </div>
                        
                        {order.items && order.items.length > 0 && (
                          <div className="border-t pt-4">
                            <div className="space-y-2">
                              {order.items.map((item) => (
                                <div key={item.id} className="flex items-center justify-between text-sm">
                                  <div className="flex items-center space-x-3">
                                    <div className="flex-shrink-0">
                                      <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                                        {getFeaturedImageUrl(item.featured_image) ? (
                                          <img
                                            src={getFeaturedImageUrl(item.featured_image)}
                                            alt={item.product_name}
                                            className="w-full h-full object-cover rounded-lg"
                                          />
                                        ) : (
                                          <span className="text-gray-500 text-xs">No Image</span>
                                        )}
                                      </div>
                                    </div>
                                    <div>
                                      <p className="font-medium text-gray-900">{item.product_name || 'Unknown Product'}</p>
                                      <p className="text-gray-500">Qty: {item.quantity || 0}</p>
                                    </div>
                                  </div>
                                  <p className="font-medium text-gray-900">${item.total ? parseFloat(item.total).toFixed(2) : '0.00'}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            <div className="bg-white shadow rounded-lg border">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Account Settings</h2>
              </div>
              
              <div className="px-6 py-4 space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-900">Password</h3>
                  <p className="text-sm text-gray-500">Update your password to keep your account secure.</p>
                  <button className="mt-2 btn btn-outline btn-sm">
                    Change Password
                  </button>
                </div>
                
                <div className="border-t pt-4">
                  <h3 className="text-sm font-medium text-gray-900">Email Notifications</h3>
                  <p className="text-sm text-gray-500">Manage your email notification preferences.</p>
                  <div className="mt-2 space-y-2">
                    <label className="flex items-center">
                      <input type="checkbox" className="rounded border-gray-300" defaultChecked />
                      <span className="ml-2 text-sm text-gray-700">Order confirmations</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" className="rounded border-gray-300" defaultChecked />
                      <span className="ml-2 text-sm text-gray-700">Shipping updates</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" className="rounded border-gray-300" />
                      <span className="ml-2 text-sm text-gray-700">Promotional emails</span>
                    </label>
                  </div>
                </div>
                
                <div className="border-t pt-4">
                  <h3 className="text-sm font-medium text-red-600">Danger Zone</h3>
                  <p className="text-sm text-gray-500">Permanently delete your account and all associated data.</p>
                  <button className="mt-2 btn btn-sm bg-red-600 text-white hover:bg-red-700">
                    Delete Account
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Profile
